'use server';

import { createSafeActionClient } from 'next-safe-action';
import { formalMemberCondition } from '@workspace/booking/member-management';

import { revalidatePath } from 'next/cache';
import { cookies } from 'next/headers';
import { randomUUID } from 'node:crypto';
import { z } from 'zod';
import {
  acceptBookingInvitation,
  cancelBooking,
  checkInBooking,
  closeAvailabilityOccurrence,
  createAvailabilitySeriesBatch,
  createBooking,
  createBookingInvitation,
  listAvailableBookingSlots,
  markBookingNoShow,
  recordManualCourseSale,
  removeBookingParticipant,
  updateAvailabilityOccurrence,
} from '@workspace/booking';
import { and, bookings, bookingResources, courseAvailabilityOccurrences, coursePlans, courseTeachers, courseVenues, db, eq, oauthAccounts, users } from '@workspace/db/drizzle';
import { getCurrentMiniAppUser } from '@/lib/auth';
import { createMemberQrToken, getMemberCode, normalizeMemberCode, verifyMemberQrToken } from '@/lib/member-qr';
import { getMiniAppStoreId } from '@/lib/store';

// 異動後更新瀏覽器分頁資料版本，讓 layout 中的暫存與伺服器同步失效。
async function revalidateMiniAppPages() {
  (await cookies()).set('miniapp_data_revision', randomUUID(), { httpOnly: true, sameSite: 'lax', path: '/' });
  revalidatePath('/', 'layout');
}

function requireString(formData: FormData, key: string) {
  const value = String(formData.get(key) ?? '').trim();
  if (!value) {
    throw new Error(`${key} 為必填`);
  }
  return value;
}

function requireAvailabilityLocations(formData: FormData) {
  const scopeValues = formData.getAll('availabilityScopes').map(String).filter(Boolean);
  if (!scopeValues.length) throw new Error('至少要選擇一間開放教室');
  if (scopeValues.length > 20) throw new Error('一次最多選擇 20 個候選教室');
  return scopeValues.map((scope) => {
    const [venueId, roomId] = scope.split(':');
    if (!venueId || !roomId || roomId === '*') throw new Error('每個開放地點都必須指定教室');
    return { venueId, roomId };
  });
}

async function requireStudentContext() {
  const [storeId, user] = await Promise.all([getMiniAppStoreId(), getCurrentMiniAppUser()]);

  if (!storeId) {
    throw new Error('MiniApp 尚未啟用或商店解析失敗');
  }

  if (!user) {
    throw new Error('請先登入 LINE 會員');
  }

  return { storeId, user };
}

async function requireTeacherContext() {
  const { storeId, user } = await requireStudentContext();
  const teacher = await db.query.courseTeachers.findFirst({
    where: and(
      eq(courseTeachers.storeId, storeId),
      eq(courseTeachers.userId, user.id),
      eq(courseTeachers.isActive, true)
    ),
    columns: { id: true },
  });

  if (!teacher) {
    throw new Error('目前登入者不是此商店老師');
  }

  return { storeId, user, teacher };
}

const teacherPaymentMethods = new Set(['CASH', 'CREDIT_CARD', 'BANK_TRANSFER']);

export async function resolveMiniAppMemberQrAction(token: string) {
  const { storeId } = await requireTeacherContext();
  const userId = await verifyMemberQrToken(token, storeId);
  const member = await db.query.users.findFirst({
    where: and(eq(users.id, userId), eq(users.storeId, storeId), formalMemberCondition),
    columns: { id: true, name: true },
  });
  if (!member) throw new Error('找不到有效的學員會員');
  return { memberId: member.id, memberName: member.name || 'LINE 會員' };
}

export async function resolveMiniAppMemberCodeAction(input: string) {
  const { storeId } = await requireTeacherContext();
  const memberCode = normalizeMemberCode(input);
  const members = await db
    .select({ id: users.id, name: users.name })
    .from(users)
    .where(and(eq(users.storeId, storeId), formalMemberCondition));
  const uniqueMembers = Array.from(new Map(members.map((member) => [member.id, member])).values());
  const matches = uniqueMembers.filter((member) => getMemberCode(storeId, member.id) === memberCode);
  if (!matches.length) throw new Error('找不到這個會員代號，請學員重新確認');
  if (matches.length > 1) throw new Error('會員代號重複，請改用會員 QR Code');
  const member = matches[0]!;
  return {
    memberId: member.id,
    memberName: member.name || 'LINE 會員',
    memberCode,
    memberToken: await createMemberQrToken(storeId, member.id),
  };
}

export async function recordMiniAppTeacherCourseSaleAction(formData: FormData) {
  const { storeId, user, teacher } = await requireTeacherContext();
  const planId = requireString(formData, 'planId');
  const memberToken = requireString(formData, 'memberToken');
  const paymentMethod = requireString(formData, 'paymentMethod');
  const receivedAmount = Math.round(Number(formData.get('receivedAmount') || 0) * 100);
  if (!teacherPaymentMethods.has(paymentMethod)) throw new Error('收款方式不正確');
  if (!Number.isInteger(receivedAmount) || receivedAmount < 0) throw new Error('收款金額不正確');

  const memberId = await verifyMemberQrToken(memberToken, storeId);
  const [member, plan] = await Promise.all([
    db.query.users.findFirst({ where: and(eq(users.id, memberId), eq(users.storeId, storeId), formalMemberCondition), columns: { id: true, name: true } }),
    db.query.coursePlans.findFirst({ where: and(eq(coursePlans.id, planId), eq(coursePlans.storeId, storeId), eq(coursePlans.status, 'ACTIVE'), eq(coursePlans.isPurchasable, true)), columns: { id: true, name: true, price: true, venueId: true } }),
  ]);
  if (!member) throw new Error('找不到有效的學員會員');
  if (!plan) throw new Error('此課程方案目前無法成交');
  const venue = plan.venueId
    ? await db.query.courseVenues.findFirst({ where: and(eq(courseVenues.id, plan.venueId), eq(courseVenues.storeId, storeId)), columns: { name: true } })
    : null;

  const result = await recordManualCourseSale({
    requestKey: z.string().uuid().parse(formData.get('requestKey')),
    storeId,
    userId: member.id,
    planId: plan.id,
    receivedAmount,
    paymentMethod,
    note: String(formData.get('note') ?? '').trim() || null,
    actor: { userId: user.id, teacherId: teacher.id },
  });
  await revalidateMiniAppPages();
  return {
    saleId: result.sale.id,
    memberName: member.name || 'LINE 會員',
    planId: plan.id,
    planName: plan.name,
    venueName: venue?.name ?? '全場館',
    totalAmount: plan.price,
    receivedAmount,
    remainingAmount: plan.price - receivedAmount,
    paymentMethod,
    note: result.sale.note,
    status: result.sale.status,
    soldAt: result.sale.soldAt.toISOString(),
  };
}

export const createMiniAppBookingAction = createSafeActionClient({
  handleServerError: () => '無法完成預約，請稍後再試',
}).use(async ({ next }) => {
  const context = await requireStudentContext();
  return next({ ctx: context });
}).schema(z.custom<FormData>((value) => value instanceof FormData).transform((form) => ({
  ...Object.fromEntries(form.entries()), resourceIds: form.getAll('resourceIds'),
})).pipe(z.object({
  teacherId: z.string().uuid(), courseId: z.string().uuid(), venueId: z.string().uuid(),
  upgradeRuleId: z.string().uuid().optional(), upgradeAmount: z.coerce.number().int().positive().optional(),
  creditId: z.string().uuid(), requestKey: z.string().uuid(),
  startsAt: z.coerce.date(), endsAt: z.coerce.date(),
  allocationMode: z.enum(['AUTO', 'MANUAL']),
  roomId: z.union([z.string().uuid(), z.literal('')]),
  resourceId: z.union([z.string().uuid(), z.literal('')]),
  resourceIds: z.array(z.string().uuid()),
  availabilityOccurrenceId: z.union([z.string().uuid(), z.literal('')]),
}))).action(async ({ parsedInput, ctx }) => {
  const { storeId, user } = ctx;
  const { teacherId, courseId, venueId, startsAt, endsAt, allocationMode, creditId, requestKey } = parsedInput;
  const availabilityOccurrenceId = parsedInput.availabilityOccurrenceId || null;

  const existing = allocationMode === 'AUTO' ? await db.query.bookings.findFirst({ where: and(eq(bookings.storeId, storeId), eq(bookings.userId, user.id), eq(bookings.requestKey, requestKey)) }) : null;
  const existingResources = existing ? await db.select({ id: bookingResources.resourceId }).from(bookingResources).where(eq(bookingResources.bookingId, existing.id)) : [];
  const candidates = existing ? [{ roomId: existing.roomId, resourceId: existing.resourceId, resourceIds: existingResources.map(r => r.id), availabilityOccurrenceId: existing.availabilityOccurrenceId }] : allocationMode === 'AUTO'
    ? (await listAvailableBookingSlots({ storeId, courseId, teacherId, venueId, from: startsAt, to: endsAt }))
        .filter((slot) => slot.startsAt.getTime() === startsAt.getTime()
          && slot.endsAt.getTime() === endsAt.getTime()
          && (!availabilityOccurrenceId || slot.availabilityOccurrenceId === availabilityOccurrenceId))
    : [{
        roomId: parsedInput.roomId || null,
        resourceId: parsedInput.resourceId || null,
        resourceIds: parsedInput.resourceIds,
        availabilityOccurrenceId,
      }];

  if (!candidates.length) return { ok: false as const, code: 'slot-unavailable' as const };

  for (const candidate of candidates) {
    try {
      const booking = await createBooking({
        studentSelfBooking: true,
        upgrade: parsedInput.upgradeRuleId && parsedInput.upgradeAmount ? { ruleId: parsedInput.upgradeRuleId, amount: parsedInput.upgradeAmount } : undefined,
        requestKey,
        creditId,
        storeId,
        userId: user.id,
        teacherId,
        courseId,
        venueId,
        roomId: candidate.roomId,
        resourceId: candidate.resourceId,
        resourceIds: candidate.resourceIds,
        availabilityOccurrenceId: candidate.availabilityOccurrenceId,
        startsAt,
        endsAt,
        metadata: { source: 'line-miniapp', allocationMode },
      });

      try { await revalidateMiniAppPages(); revalidatePath('/booking'); revalidatePath('/records'); }
      catch { console.warn('預約已完成，快取將於下次讀取時更新'); }
      return { ok: true as const, bookingId: booking.id };
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('升級') || message.includes('加價')) return { ok: false as const, code: 'upgrade-changed' as const };
      if (message.includes('需由老師或櫃台')) return { ok: false as const, code: 'staff-booking-only' as const };
      if (message.includes('課程額度不足')) return { ok: false as const, code: 'credit-insufficient' as const };
      const slotUnavailable = ['老師該時段已被預約', '該位置已被預約', '此預約時段無效或已不可預約', '課程未開放預約']
        .some((text) => message.includes(text));
      if (!slotUnavailable) return { ok: false as const, code: 'unknown' as const };
    }
  }
  return { ok: false as const, code: 'slot-unavailable' as const };
});

export async function cancelMiniAppBookingAction(formData: FormData) {
  const { storeId, user } = await requireStudentContext();

  await cancelBooking({
    storeId,
    bookingId: requireString(formData, 'bookingId'),
    reason: '學員從 LINE MiniApp 取消',
    refundCredit: true,
    actor: { userId: user.id },
  });

  await revalidateMiniAppPages();
}

export async function checkInMiniAppBookingAction(formData: FormData) {
  const { storeId, user, teacher } = await requireTeacherContext();

  await checkInBooking({
    storeId,
    bookingId: requireString(formData, 'bookingId'),
    teacherId: teacher.id,
    actor: { userId: user.id },
    attendedUserIds: formData.getAll('attendedUserIds').map(String).filter(Boolean),
    attendedGuestCount: z.coerce.number().int().min(0).parse(formData.get('attendedGuestCount') ?? 0),
    scannerContext: {
      source: 'line-miniapp-manual-verification',
    },
  });

  await revalidateMiniAppPages();
}

export async function markMiniAppBookingNoShowAction(formData: FormData) {
  const { storeId, user, teacher } = await requireTeacherContext();
  await markBookingNoShow({
    storeId,
    bookingId: requireString(formData, 'bookingId'),
    teacherId: teacher.id,
    actor: { userId: user.id },
  });
  await revalidateMiniAppPages();
}

export async function createMiniAppBookingInvitationAction(formData: FormData) {
  const { storeId, user } = await requireStudentContext();
  const teacher = await db.query.courseTeachers.findFirst({
    where: and(eq(courseTeachers.storeId, storeId), eq(courseTeachers.userId, user.id), eq(courseTeachers.isActive, true)),
    columns: { id: true },
  });
  return createBookingInvitation({
    storeId,
    bookingId: requireString(formData, 'bookingId'),
    createdByUserId: user.id,
    teacherId: teacher?.id ?? null,
  });
}

export async function acceptMiniAppBookingInvitationAction(formData: FormData) {
  const { storeId, user } = await requireStudentContext();
  const result = await acceptBookingInvitation({ storeId, userId: user.id, token: requireString(formData, 'token') });
  await revalidateMiniAppPages();
  return result;
}

export async function removeMiniAppBookingParticipantAction(formData: FormData) {
  const { storeId, user } = await requireStudentContext();
  await removeBookingParticipant({
    storeId,
    bookingId: requireString(formData, 'bookingId'),
    participantUserId: requireString(formData, 'participantUserId'),
    actorUserId: user.id,
  });
  await revalidateMiniAppPages();
}

export async function createMiniAppTeacherAvailabilityAction(formData: FormData) {
  const { storeId, user, teacher } = await requireTeacherContext();
  const frequency = requireString(formData, 'frequency') as 'NONE' | 'DAILY' | 'WEEKLY';
  const until = String(formData.get('until') ?? '').trim();
  const { startsAt, endsAt } = requireMiniAppAvailabilityRange(formData);
  const locations = requireAvailabilityLocations(formData);
  const courseIds = formData.getAll('courseIds').map(String).filter(Boolean);
  if (!courseIds.length) throw new Error('至少要選擇一門可教授課程');
  await createAvailabilitySeriesBatch({
    storeId,
    teacherId: teacher.id,
    locations,
    courseIds,
    startsAt,
    endsAt,
    minBookingNoticeMinutes: Number(formData.get('minBookingNoticeMinutes') || 120),
    recurrence: {
      frequency,
      interval: Number(formData.get('interval') || 1),
      occurrenceCount: frequency === 'NONE' ? 1 : Number(formData.get('occurrenceCount') || 1),
      until: until ? new Date(`${until}T23:59:59`) : null,
      weekdays: formData.getAll('weekdays').map(Number),
    },
    actor: { userId: user.id },
  });
  await revalidateMiniAppPages();
}

export async function closeMiniAppTeacherAvailabilityAction(formData: FormData) {
  const { storeId, teacher } = await requireTeacherContext();
  const occurrenceId = requireString(formData, 'occurrenceId');
  const [owned] = await db.select({ id: courseAvailabilityOccurrences.id }).from(courseAvailabilityOccurrences).where(and(
    eq(courseAvailabilityOccurrences.storeId, storeId),
    eq(courseAvailabilityOccurrences.id, occurrenceId),
    eq(courseAvailabilityOccurrences.teacherId, teacher.id),
  )).limit(1);
  if (!owned) throw new Error('無權調整其他老師的開放區間');
  await closeAvailabilityOccurrence({
    storeId,
    occurrenceId,
    scope: requireString(formData, 'scope') as 'SINGLE' | 'FOLLOWING' | 'SERIES',
  });
  await revalidateMiniAppPages();
}

export async function updateMiniAppTeacherAvailabilityAction(formData: FormData) {
  const { storeId, teacher } = await requireTeacherContext();
  const occurrenceId = requireString(formData, 'occurrenceId');
  const { startsAt, endsAt } = requireMiniAppAvailabilityRange(formData);
  const [owned] = await db.select({ id: courseAvailabilityOccurrences.id }).from(courseAvailabilityOccurrences).where(and(
    eq(courseAvailabilityOccurrences.storeId, storeId),
    eq(courseAvailabilityOccurrences.id, occurrenceId),
    eq(courseAvailabilityOccurrences.teacherId, teacher.id),
  )).limit(1);
  if (!owned) throw new Error('無權調整其他老師的開放區間');
  await updateAvailabilityOccurrence({
    storeId,
    occurrenceId,
    scope: requireString(formData, 'scope') as 'SINGLE' | 'FOLLOWING' | 'SERIES',
    startsAt,
    endsAt,
    locations: requireAvailabilityLocations(formData),
    courseIds: formData.getAll('courseIds').map(String).filter(Boolean),
    minBookingNoticeMinutes: Number(formData.get('minBookingNoticeMinutes') || 120),
  });
  await revalidateMiniAppPages();
}

function requireMiniAppAvailabilityRange(formData: FormData) {
  const startsAt = new Date(requireString(formData, 'startsAt'));
  const endsAt = new Date(requireString(formData, 'endsAt'));
  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    throw new Error('請選擇有效的開始與結束時間');
  }
  if (startsAt.getMinutes() % 30 !== 0 || endsAt.getMinutes() % 30 !== 0) {
    throw new Error('開放時間的分鐘只能選擇 00 或 30 分');
  }
  if (endsAt.getTime() - startsAt.getTime() < 60 * 60 * 1000) {
    throw new Error('開放區間至少需要 1 小時');
  }
  return { startsAt, endsAt };
}
